import {Component, OnInit, ViewChild} from '@angular/core';
import {SnackBarService} from '../../../services/common/snack-bar/snack-bar.service';
import {UserPrivilegeService} from '../../../services/common/user-privilege/user-privilege.service';
import {MatDialog} from '@angular/material/dialog';
import {OperatorsSettingsApiService} from '../../../services/api/settings/administration/operators/operators-settings-api.service';
import {ConfirmationDialogComponent} from '../../../dialogs/confirmation-dialog/confirmation-dialog.component';
import * as _ from 'lodash';
import {OperatorsDialogComponent} from './operators-dialog/operators-dialog.component';
import {OperatorRoleType} from '../../../services/enums/operator-role-type.enum';
import {SearchTermDefinitionInf} from '../../../interfaces/common/search/search-term-definition-inf';
import {OperatorsSearchComponent} from './operators-search/operators-search.component';
import {ArmTableComponent} from '../../../components/arm-table/arm-table.component';
import {AcDialogService, PromiseService, SessionService} from 'ac-infra';
import {LoaderService} from '../../../services/common/loader-service/loader.service';
import {ArmSession} from '../../../login/login.component';
import {SecurityPoliciesApiService} from '../../../services/api/settings/administration/security-policies/security-policies-api.service';
import {forkJoin} from 'rxjs';
import {RoleTypeEnum} from '../../../services/enums/role-type.enum';
import Search from '../../../utils/search';
import {LoginApiService} from '../../../services/api/login/login-api.service';

@Component({
  selector: 'operators-settings',
  templateUrl: './operators-settings.component.html',
  styleUrls: ['./operators-settings.component.scss']
})
export class OperatorsSettingsComponent implements OnInit {

  @ViewChild(ArmTableComponent) armTable;

  selection;
  isUnauthorized: boolean;
  search: any = {};
  items = [];
  securityPolicies;
  securityPoliciesOptions;

  operatorsSearchComponent = OperatorsSearchComponent;

  displayedColumns: any[] = [
    {name: 'userName', display: 'name' , selected: true},
    {name: 'securityPolicies', display: 'Security Policies', selected: true}
  ];

  searchTerms: SearchTermDefinitionInf[];

  private typeFilter = {
    operator: '=',
    value: 'OPERATOR'
  };

  searchData;
  user: ArmSession;
  isSecurityAdmin: boolean;
  isLoginSecureEnable: boolean;

  constructor(private operatorsSettingsApiService: OperatorsSettingsApiService,
              private securityPoliciesApiService: SecurityPoliciesApiService,
              private snackBarService: SnackBarService,
              private userPrivilege: UserPrivilegeService,
              private dialog: MatDialog,
              private acDialogService: AcDialogService,
              private loaderService: LoaderService,
              private sessionService: SessionService,
              private loginApiService: LoginApiService ){
    this.user = this.sessionService.activeSession;
  }


  ngOnInit(): void {
    this.loaderService.showMainContainer();
    this.isUnauthorized = !this.userPrivilege.isAuthorized();
    this.isSecurityAdmin = this.userPrivilege.isAuthorized(RoleTypeEnum.SECURITY_ADMIN);
    this.loginApiService.getLoginSecurityParams().then(response => {this.isLoginSecureEnable = response.enabled; });
    this.refresh();
  }

  onSelection(selection: any): void {
    this.selection = selection;
  }

  addItem(): void {
    const dialogData = {
      isEditMode: false,
      securityPolicies: this.securityPolicies,
      selectedItem: {
        userName: null,
        passwordConfirm: null,
        role: OperatorRoleType.ADMIN
      },
      isLoginSecureEnable : this.isLoginSecureEnable
    };

    this.acDialogService.open(OperatorsDialogComponent, {
      dialogData,
      title: 'Add Operator',
      onSubmit: () => {
        delete dialogData.selectedItem.passwordConfirm;
        this.operatorsSettingsApiService.addOperator(dialogData.selectedItem).toPromise().then(() => {
          this.snackBarService.display(`"${dialogData.selectedItem.userName}" successfully added`);
          this.refresh();
        });
      }
    });
  }

  editItem(): void {
    const dialogData = {
      isEditMode: true,
      securityPolicies: this.securityPolicies,
      selectedItem:  _.cloneDeep(this.selection),
      isLoginSecureEnable : this.isLoginSecureEnable
    };

    this.acDialogService.open(OperatorsDialogComponent, {
      dialogData,
      title: 'Edit Operator',
      preSubmit: () => {
        if (this.isCurrentUser()) {
          const defer = PromiseService.defer();

          const confirmationDialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: {
              message: 'You are about to update the currently logged in operator. \n' +
                'You will be redirected to the login page, do you want to proceed?'
            }
          });

          confirmationDialogRef.componentInstance.onApply.subscribe(() => {
            confirmationDialogRef.close();
            defer.resolve();
          });

          confirmationDialogRef.componentInstance.onCancel.subscribe(() => {
            confirmationDialogRef.close();
            defer.reject();
          });

          return defer.promise;
        }
      },
      onSubmit: () => {
        delete dialogData.selectedItem.passwordConfirm;
        delete dialogData.selectedItem.securityPolicies;

        this.operatorsSettingsApiService.updateOperator(dialogData.selectedItem).toPromise().then(() => {
          this.snackBarService.display(`"${dialogData.selectedItem.userName}" successfully edited`);
          this.refresh();

          if (this.isCurrentUser()) {
            this.sessionService.endSession();
          }
        });
      }
    });
  }

  removeItem(): void {
    const deleteCurrentUserMessage = `Are you sure you want to delete the currently logged in operator: "${this.selection.userName}"?
     You will be redirected to the login page, do you want to proceed?`;

    const deleteUserMessage = `Are you sure you want to delete the operator: "${this.selection.userName}"?`;

    const isCurrentUser = this.isCurrentUser();

    const confirmationMessage = isCurrentUser ? deleteCurrentUserMessage : deleteUserMessage;

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message: confirmationMessage
      }
    });

    dialogRef.componentInstance.onApply.subscribe(() => {
      this.operatorsSettingsApiService.deleteOperator(this.selection.id).toPromise().then(() => {
        dialogRef.close();
        this.refresh();
        this.snackBarService.display(`${this.selection.userName} successfully deleted`);

        if (isCurrentUser) {
          this.sessionService.endSession();
        }
      });
    });
  }

  isCurrentUser(): boolean {
    if (this.selection && !!this.user) {
      return this.user.userName === this.selection.userName;
    }

    return false;
  }

  refresh(): void {
    let customersParams: any = {};

    if (!_.isEmpty(this.search.filter)) {
      customersParams = {
        filter: this.search.filter
      };
      if (_.isEmpty(this.search.filter.roles)) {
        customersParams.filter.type = this.typeFilter;
      }
    } else {
      customersParams = {
        filter: {
          type: this.typeFilter
        }
      };
    }

    forkJoin([
      this.securityPoliciesApiService.getSecurityPolicies(),
      this.operatorsSettingsApiService.getOperators(customersParams)
    ]).subscribe(data => {
      this.securityPolicies = data[0];

      data[1].forEach((operator) => {
        operator.securityPolicies = operator.securityPolicyIds.map(securityPolicyId => {
          return this.securityPolicies.find(securityPolicy => securityPolicy.id === securityPolicyId).name;
        }).join(', ');
      });
      this.items = data[1];
      this.loaderService.hideMainContainer();
      this.setSearchOptions(this.securityPolicies);
    });
  }

  isActionDisabled(): boolean {
    return !this.selection || this.isUnauthorized;
  }

  onSearch(data: any): void {
    this.search = data;
    this.armTable.firstPage();

    this.refresh();
  }

  private setSearchOptions(securityPolicies): void {
    this.securityPoliciesOptions = Search.generateSearchOptions(securityPolicies, 'name', 'id');
    this.searchData = {
      securityPolicies: this.securityPoliciesOptions
    };

    this.searchTerms = [
      {term: 'userName', operator: ':', displayName: 'Name'},
      {term: 'securityPolicyId', operator: '=', displayName: 'Security Policies',
        displayValue: Search.getOptionValue(this.securityPoliciesOptions)}
    ];
  }
}
